import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import "./ToolboxMeetingMinutes.css";
import { useAuth } from './AuthContext';
import API from './services/api';
import { getAppPath, getStorageItem, removeStorageItem, setStorageItem } from './utils/runtimeMode';

const TEAL = "#1fa97a";
const TEAL_BG = "#e8f7f2";

const FALLBACK_DEPARTMENTS = [
  "Communications","Corporate Services","Legislative Services",
  "Municipal Enforcement","Parks","Planning and Development",
  "Recreation","Roads and Operations","Utilities"
];

const FALLBACK_TOOLBOX_CATEGORIES = [
  {id:"3_worker_rights",label:"3 Worker Rights",sub:null},
  {id:"ab_ohs_legislation",label:"AB OHS Legislation",sub:null},
  {id:"animal_people_encounters",label:"Animal & People Encounters",sub:null},
  {id:"chainsaw_safety",label:"Chainsaw Safety",sub:null},
  {id:"confined_space_entry_gas_detection",label:"Confined Space Entry & Gas Detection",sub:null},
  {id:"due_diligence",label:"Due Diligence",sub:null},
  {id:"emergency_response_procedures_fire_marshal_trainings",label:"Emergency Response Procedures, Fire Marshall Trainings",sub:null},
  {id:"ergonomics",label:"Ergonomics",sub:null},
  {id:"fall_protection_plan",label:"Fall Protection Plan",sub:null},
  {id:"fire_extinguisher_training",label:"Fire Extinguisher Training",sub:null},
  {id:"harassment_violence_policy_procedures",label:"Harassment & Violence Policy & Procedures",sub:null},
  {id:"health_safety_policy",label:"Health and Safety Policy",sub:null},
  {id:"housekeeping",label:"Housekeeping",sub:null},
  {id:"incident_near_miss_reporting",label:"Incident & Near Miss Reporting",sub:null},
  {id:"lock_out_tag_out",label:"Lock Out/Tag Out",sub:null},
  {id:"robbery_prevention",label:"Robbery Prevention",sub:null},
  {id:"safe_driving_practices",label:"Safe Driving Practices",sub:null},
  {id:"safe_work_procedures",label:"Safe Work Procedures",sub:"Describe procedure reviewed"},
  {id:"safety_committee_training",label:"Safety Committee Training",sub:null},
  {id:"slips_trips_falls",label:"Slips, trips and falls",sub:null},
  {id:"supervisors_ohs_responsibilities",label:"Supervisors OHS Responsibilities",sub:null},
  {id:"tick_awareness",label:"Tick Awareness",sub:null},
  {id:"whmis_2015",label:"WHMIS 2015",sub:"Describe products safety data sheets reviewed"},
  {id:"working_alone_call_in_procedures",label:"Working Alone, Call-in procedures",sub:null},
  {id:"working_outdoors_heat_cold_procedures",label:"Working Outdoors, Heat/Cold Procedures",sub:null},
  {id:"workplace_inspections",label:"Workplace Inspections",sub:null},
  {id:"other",label:"Other",sub:"Describe topic reviewed"}
];

const TOOLBOX_TOPIC_HINTS = {
  safe_work_procedures: "Describe procedure reviewed",
  whmis_2015: "Describe products safety data sheets reviewed",
  other: "Describe topic reviewed"
};

const TOOLBOX_CATEGORY_PAGE_SIZE = 6;
const INSPECTION_REPORTS_PAGE_SIZE = 5;

const DRAFT_STORAGE_KEY = 'toolbox-meeting-minutes-draft';

const formatUserLabel = (user) => {
  const name = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const email = user?.email || '';
  const department = user?.department || user?.departmentName || '';
  const parts = [name || email].filter(Boolean);
  if (department) {
    parts.push(department);
  }
  if (email && name && email !== name) {
    parts[0] = `${name} (${email})`;
  }
  return parts.filter(Boolean).join(' • ');
};

const formatPrequalificationContractorContact = (record = {}) => {
  const contactName = String(record.primaryContactName || record.PRIMARY_CONTACT_NAME || '').trim();
  const contactPhone = String(record.primaryContactPhone || record.PRIMARY_CONTACT_PHONE || '').trim();
  const contactEmail = String(record.primaryContactEmail || record.PRIMARY_CONTACT_EMAIL || '').trim();
  const nameAndPhone = [contactName, contactPhone].filter(Boolean).join(' / ');

  return nameAndPhone || contactEmail || 'Contact not provided';
};

const mapPrequalificationContractorOption = (record = {}) => ({
  id: `prequal-${record.id || record.ID}`,
  name: String(record.companyName || record.COMPANY_NAME || 'Contractor').trim(),
  contact: formatPrequalificationContractorContact(record),
  source: 'contractor-prequalification',
  contractorPrequalificationId: record.id || record.ID
});

const HARD_CODED_CONTRACTORS = [
  { id: 1, name: 'Acapulco Pools', contact: 'Mark Elliott - (519) 830-8201', email: 'mark@acapulcopools.com', insuranceExpiry: 'Nov 01, 2023', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 2, name: 'Ainsworth Inc.', contact: 'Trevor Kaiser - (403) 265-6750', email: 'trevor.kaiser@ainsworth.com', insuranceExpiry: 'Oct 31, 2023', licenseNumber: '20230077', licenseExpiry: '31-Dec-2023' },
  { id: 3, name: 'Alsa Road Construction Ltd.', contact: 'David Mac - 403-243-9313', email: 'davidmac@alsaroad.com', insuranceExpiry: 'Feb 01, 2023', licenseNumber: '20240264', licenseExpiry: '31-Dec-2024' },
  { id: 4, name: 'Alta Vac Solutions Ltd', contact: 'Shaun Madigan - (587) 338-1941', email: 'shaun@altavs.ca', insuranceExpiry: 'May 23, 2023', licenseNumber: '20220275', licenseExpiry: '31-Dec-2022' },
  { id: 5, name: 'Aquatech Diving & Marine Services', contact: 'Steve Berube - (780) 913-6690', email: 'info@aquatechdiving.ca', insuranceExpiry: 'Jun 24, 2024', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 34, name: 'Aquatera Utilities Inc.', contact: 'Leo (Leonel) Tobias - ltobias@aquatera.ca', email: 'ltobias@aquatera.ca', insuranceExpiry: '', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 6, name: 'BB Services Ltd.', contact: 'Ken Barrow - (403) 899-8401', email: 'bbservices@telus.net', insuranceExpiry: 'Jun 13, 2024', licenseNumber: '20240111', licenseExpiry: '31-Dec-2024' },
  { id: 7, name: 'Berc Mechanical Ltd.', contact: 'Berkley Crowe - 403-968-9109', email: 'info@bercmech.com', insuranceExpiry: '', licenseNumber: '20240181', licenseExpiry: '31-Dec-2024' },
  { id: 8, name: 'Big Hill Services Ltd.', contact: '403-932-3598', email: 'info@bighillservices.com', insuranceExpiry: 'Oct 29, 2022', licenseNumber: '2021248', licenseExpiry: '16-Jun-2021' },
  { id: 9, name: 'Bow Mark Paving Ltd.', contact: 'Trevor Forbes - (403) 816-9767', email: 'trevor@bowmark.ca', insuranceExpiry: 'Apr 01, 2024', licenseNumber: '20240303', licenseExpiry: '31-Dec-2024' },
  { id: 10, name: 'Boys & Girls Clubs of the Foothills', contact: 'Shirley Puttock - (403) 933-4066', email: 'exdirect@telus.net', insuranceExpiry: 'Apr 01, 2021', licenseNumber: '20240099', licenseExpiry: '31-Dec-2024' },
  { id: 11, name: 'Carmichael Engineering', contact: 'Chris Brown - 403-255-3322', email: 'cbrown@carmichael-eng.ca', insuranceExpiry: '', licenseNumber: '20230126', licenseExpiry: '31-Dec-2023' },
  { id: 12, name: 'CDM Mechanical Ltd.', contact: 'Mark Spak - (403) 652-1777', email: 'mark@cdmmechanical.com', insuranceExpiry: 'Mar 01, 2025', licenseNumber: '20240068', licenseExpiry: '31-Dec-2024' },
  { id: 13, name: 'Chamco Industries', contact: 'Erin Duddy - (403) 777-0835', email: 'eduddy@chamco.com', insuranceExpiry: 'Jul 01, 2025', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 14, name: 'Chikeez Chow Down', contact: 'Patricia Chikowski - (403) 498-4461', email: 'chikeezchowdown@shaw.ca', insuranceExpiry: 'Jun 21, 2024', licenseNumber: '20240393', licenseExpiry: '31-Dec-2024' },
  { id: 15, name: 'CK Fire and Safety Solutions Inc.', contact: 'Chad Kalynko - (430) 805-4042', email: 'infor@ckfireandsafety.com', insuranceExpiry: 'Sep 12, 2023', licenseNumber: '20230331', licenseExpiry: '15-Jun-2023' },
  { id: 16, name: 'Constant Fire Protection Systems Ltd.', contact: 'Paul Haddow - 403-512-9164', email: 'paul.haddow@cfps.ca', insuranceExpiry: 'Jun 15, 2022', licenseNumber: '20220108', licenseExpiry: '31-Dec-2022' },
  { id: 17, name: 'DAVG Consulting Inc.', contact: 'Doreen von Gradulewski - (403) 874-4107', email: 'safety@diamondvalley.town', insuranceExpiry: 'Jan 17, 2024', licenseNumber: '20240030', licenseExpiry: '31-Dec-2024' },
  { id: 18, name: 'Dexon Canada Air Systems Inc.', contact: 'Will Dumonceaux - 403-605-0917', email: 'sales@dexoncanada.com', insuranceExpiry: 'Apr 14, 2022', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 19, name: 'Everest Construction', contact: 'Kelley Risdon - (403) 370-4506', email: 'info@everestconstruction.ca', insuranceExpiry: 'Jul 31, 2023', licenseNumber: '20230008', licenseExpiry: '31-Dec-2023' },
  { id: 20, name: 'Geolyn Pipe Inspection Services Ltd.', contact: 'Ken Lucas - 403-453-0522', email: 'klucas@geolyn.ca', insuranceExpiry: '', licenseNumber: '2021041', licenseExpiry: '31-Dec-2021' },
  { id: 21, name: 'GLV Travellers Inc.', contact: 'Garrett Vogelaar - (780) 951-0224', email: 'garrett@glvt.ca', insuranceExpiry: 'Apr 25, 2026', licenseNumber: '20240364', licenseExpiry: '31-Dec-2024' },
  { id: 22, name: 'Gran-Lee Electric Ltd', contact: 'Grant - (403) 312-6505', email: 'granlee@granlee.ca', insuranceExpiry: 'Feb 04, 2023', licenseNumber: '2019095', licenseExpiry: '31-Dec-2019' },
  { id: 23, name: 'High Country Vac Services', contact: 'Travis Johnson - (403) 888-3978', email: 'travis@highcountryvac.com', insuranceExpiry: 'Mar 03, 2024', licenseNumber: '20240127', licenseExpiry: '31-Dec-2024' },
  { id: 24, name: 'Insituform Technologies Ltd.', contact: 'Brent Clark - 780-893-9573', email: '', insuranceExpiry: '', licenseNumber: '2021220', licenseExpiry: '31-Dec-2021' },
  { id: 25, name: 'Kidco Construction Ltd.', contact: 'Moe Issa - 403-809-2972', email: 'missa@kidco.ca', insuranceExpiry: '', licenseNumber: '20220193', licenseExpiry: '31-Dec-2022' },
  { id: 26, name: 'Little Chinook', contact: 'Randy Stuart - (403) 540-2025', email: 'lchinook@telus.net', insuranceExpiry: 'Jun 01, 2023', licenseNumber: '20240123', licenseExpiry: '31-Dec-2024' },
  { id: 27, name: 'Lodgepole Horticultural Services Inc.', contact: 'Ali Cannon - (403) 862-5659', email: 'lodgepole@telus.net', insuranceExpiry: 'Oct 01, 2021', licenseNumber: '20220244', licenseExpiry: '31-Dec-2022' },
  { id: 28, name: 'Markr Group', contact: 'Suchit Sawant - (250) 712-3393', email: 'suchit.swant@markrgroup.com', insuranceExpiry: 'May 30, 2024', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 29, name: 'Maverick Electrical Service Inc.', contact: 'Dave Hanrahan - 403-510-8112', email: 'dhanrahan@hotmail.com', insuranceExpiry: 'Mar 13, 2023', licenseNumber: '20240143', licenseExpiry: '31-Dec-2024' },
  { id: 30, name: 'MPE Engineering Ltd. (EnGlobe Corp)', contact: 'Jill Hardy - (403) 219-6319', email: 'jhardy@mpe.ca', insuranceExpiry: 'Aug 06, 2023', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 31, name: 'MTK Construction Ltd.', contact: 'Mitch Kratchmer - (403) 634-1772', email: 'mitch@mtkltd.com', insuranceExpiry: 'Jun 27, 2023', licenseNumber: '2021155', licenseExpiry: '31-Dec-2021' },
  { id: 32, name: 'Norstar Service Solutions', contact: 'Sean Fitzsimmons - (403) 771-7791', email: 'seanfitz@norstarsolutions.com', insuranceExpiry: 'Apr 15, 2023', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 33, name: "O'Leary Excavating Ltd.", contact: 'Joey Santelli - (403) 899-8320', email: 'joey.s@olearyexcavating.com', insuranceExpiry: 'Mar 25, 2024', licenseNumber: '20240240', licenseExpiry: '31-Dec-2024' },
  { id: 46, name: 'Park Enterprises Ltd.', contact: 'contact@parkenterprises.ca - (403) 329-3747', email: 'contact@parkenterprises.ca', insuranceExpiry: '', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 47, name: 'Property Assessor', contact: 'Doug Jensen - (403) 315-1324', email: '', insuranceExpiry: '', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 35, name: 'Ridgeline Electrical Services Ltd', contact: 'Kimberly Goertzen - (403) 998-1429', email: 'kgoertzen@ridgelineelectrical.com', insuranceExpiry: 'Oct 13, 2023', licenseNumber: '20240361', licenseExpiry: '31-Dec-2024' },
  { id: 36, name: 'Russpet Construction Ltd', contact: 'Russell Peterson - (403) 291-4404', email: 'russell@russpet.com', insuranceExpiry: 'Jul 30, 2022', licenseNumber: '20220207', licenseExpiry: '30-Jun-2022' },
  { id: 37, name: 'Sharon Young (1951901 Alberta Ltd.)', contact: 'Sharon Young - (403) 863-5150', email: 'SY1951901AlbertaLtd@shaw.ca', insuranceExpiry: 'Feb 03, 2024', licenseNumber: 'No records', licenseExpiry: 'Professional CRSP & Auditing' },
  { id: 38, name: 'Shawne Excavating & Trucking Ltd.', contact: 'Wes Shaw - (403) 321-3975', email: 'wes@shawneexcavating.com', insuranceExpiry: 'Jul 17, 2025', licenseNumber: '20240093', licenseExpiry: '31-Dec-2024' },
  { id: 39, name: 'Slickforce Mechanical Ltd.', contact: 'Brad Price - (403) 312-8013', email: 'slickforce24@gmail.com', insuranceExpiry: 'Dec 11, 2023', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 40, name: 'Superior Fencing - Lee Brothers', contact: 'Patrick Lee - (403) 617-7360', email: 'superiorfeincing@shaw.ca', insuranceExpiry: 'Apr 22, 2024', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 41, name: 'The Patchmaster Ltd.', contact: 'Danielle Mayhew - (403) 512-7915', email: 'dmayhew@thepatchmaster.ca', insuranceExpiry: 'Nov 25, 2025', licenseNumber: '20240366', licenseExpiry: '16-Oct-2024' },
  { id: 42, name: 'Titan Eavestroughing', contact: 'Kirsten - (403) 922-0258', email: 'titaneavestroughing@outlook.com', insuranceExpiry: 'Sep 21, 2022', licenseNumber: 'No records', licenseExpiry: '' },
  { id: 43, name: 'Trane Canada ULC', contact: 'Brian Erdman - 403-660-8352', email: 'bryan123@telus.net', insuranceExpiry: 'Apr 17, 2022', licenseNumber: '20230113', licenseExpiry: '31-Dec-2023' },
  { id: 44, name: 'True Grit Welding Ltd.', contact: 'Jaycee Zeer - 403-652-6493', email: 'jzeer@telus.net', insuranceExpiry: 'Mar 13, 2023', licenseNumber: '20240262', licenseExpiry: '31-Dec-2024' },
  { id: 45, name: 'Urban Systems Ltd.', contact: 'Meghan Aebig - (403) 291-1193', email: 'maebig@urbansystems.ca', insuranceExpiry: 'Jun 30, 2024', licenseNumber: 'No records', licenseExpiry: 'Professional Engineer' }
];

const normalizeBooleanValue = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    return ['true', 'yes', '1', 'y'].includes(value.trim().toLowerCase());
  }
  return !!value;
};

const getInspectionCategory = (inspection) => String(inspection?.category || inspection?.CATEGORY || inspection?.dept || '').trim();
const getInspectionDateOnly = (inspection) => {
  const rawDate = inspection?.inspectionDate || inspection?.INSPECTION_DATE || inspection?.date || '';
  if (!rawDate) return '';
  const dateText = String(rawDate).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(dateText)) return dateText.slice(0, 10);
  const parsed = new Date(dateText);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
};
const getInspectionLocation = (inspection) => [inspection?.address || inspection?.LOCATION_ADDRESS, inspection?.name || inspection?.LOCATION_NAME]
  .map((value) => String(value || '').trim())
  .filter(Boolean)
  .join(' — ');
const getInspectionHasDeficiencies = (inspection) => normalizeBooleanValue(inspection?.hasDeficiencies ?? inspection?.HAS_DEFICIENCIES);
const getInspectionPdfUrl = (inspection) => inspection?.id ? getAppPath(`/inspection-history?tab=app&inspectionId=${encodeURIComponent(inspection.id)}`) : '';

const mapInspectionListItem = (row) => ({
  id: row.INSPECTION_ID || row.id,
  date: `${row.INSPECTION_DATE || row.date || ''}${row.INSPECTION_TIME || row.time ? ` ${row.INSPECTION_TIME || row.time}` : ''}`.trim(),
  inspectionDate: row.INSPECTION_DATE || row.date || '',
  category: row.CATEGORY || row.PRIMARY_DEPARTMENT || row.dept || '',
  dept: row.CATEGORY || row.PRIMARY_DEPARTMENT || row.dept || '',
  address: row.LOCATION_ADDRESS || row.address || '',
  name: row.LOCATION_NAME || row.name || '',
  hasDeficiencies: normalizeBooleanValue(row.HAS_DEFICIENCIES ?? row.hasDeficiencies),
  label: row.label || `${`${row.INSPECTION_DATE || row.date || ''}${row.INSPECTION_TIME || row.time ? ` ${row.INSPECTION_TIME || row.time}` : ''}`.trim()}
• ${row.CATEGORY || row.PRIMARY_DEPARTMENT || row.dept || ''}
• ${row.LOCATION_ADDRESS || row.address || ''} — ${row.LOCATION_NAME || row.name || ''}`.trim()
});

const mapIncidentListItem = (row) => ({
  id: row.INCIDENT_ID || row.id,
  date: `${row.DATE || row.date || ''}${row.TIME || row.time ? ` ${row.TIME || row.time}` : ''}`.trim(),
  type: row.TITLE || row.type || 'Incident Report',
  dept: row.PRIMARY_DEPARTMENT || row.department || row.dept || ''
});

const mapMinutesHistoryItem = (row) => ({
  meetingId: row.meetingId || row.MEETING_ID || row.id,
  date: row.meetingDate || row.MEETING_DATE || '',
  facilitator: row.createdBy || row.CREATED_BY || row.facilitator || row.user || 'Unknown',
  bundleAttachmentId: row.bundleAttachmentId || row.BUNDLE_ATTACHMENT_ID || '',
  pdfUrl: row.bundleUrl || row.bundleURL || row.BUNDLE_URL || row.pdfUrl || row.PDF_URL || ''
});

const mapMinutesHistoryResponse = (data) => {
  const apiHistory = Array.isArray(data?.minutes) ? data.minutes : [];
  return apiHistory.map(mapMinutesHistoryItem).filter((item) => item.meetingId);
};

const mapMinutesDraftItem = (row) => ({
  meetingId: row.meetingId || row.MEETING_ID || row.id,
  date: row.meetingDate || row.MEETING_DATE || '',
  time: row.meetingTime || row.MEETING_TIME || '',
  location: row.location || row.LOCATION || '',
  createdBy: row.createdBy || row.CREATED_BY || 'Unknown',
  updatedAt: row.updatedAt || row.UPDATED_AT || '',
  bundleUrl: row.bundleUrl || row.BUNDLE_URL || '',
  attachmentCount: Array.isArray(row.attachments) ? row.attachments.length : Number(row.attachmentCount || row.ATTACHMENT_COUNT || 0) || 0
});

const normalizeAttachmentEntries = (attachments) => (Array.isArray(attachments) ? attachments : [])
  .map((attachment) => {
    if (typeof attachment === 'string') {
      return { id: attachment, name: attachment, url: '' };
    }

    if (!attachment || typeof attachment !== 'object') {
      return null;
    }

    const id = attachment.id || attachment.attachmentId || attachment.fileId || attachment.attachmentID || attachment.ATTACHMENT_ID || attachment.FILE_ID || '';
    const url = attachment.url || attachment.attachmentUrl || attachment.downloadUrl || '';

    return {
      ...attachment,
      id,
      attachmentId: attachment.attachmentId || id,
      fileId: attachment.fileId || id,
      url
    };
  })
  .filter((attachment) => attachment && attachment.id);

const getAttachmentIds = (attachments) => normalizeAttachmentEntries(attachments)
  .map((attachment) => attachment.id || attachment.attachmentId || attachment.fileId)
  .filter(Boolean);

const today = new Date();
const fmt2 = (n) => String(n).padStart(2, "0");
const INIT_DATE = `${today.getFullYear()}-${fmt2(today.getMonth()+1)}-${fmt2(today.getDate())}`;
const INIT_TIME = `${fmt2(today.getHours())}:${fmt2(today.getMinutes())}`;
const DISPLAY_TIME = `${fmt2(today.getHours() % 12 || 12)} : ${fmt2(today.getMinutes())} ${today.getHours() >= 12 ? "PM" : "AM"}`;

// ── Shared styles ──────────────────────────────────────────────────────────────
const sectionHeaderStyle = (checked) => ({
  display: "flex", alignItems: "center", gap: "10px",
  padding: "13px 16px", cursor: "pointer",
  background: checked ? TEAL_BG : "var(--hs-bg-primary)",
  transition: "background 0.15s",
});
const checkboxStyle = {
  width: "16px", height: "16px", accentColor: TEAL, cursor: "pointer", flexShrink: 0,
};
const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "7px 10px",
  fontSize: "14px", border: "0.5px solid var(--hs-border-secondary)",
  borderRadius: "6px", background: "var(--hs-bg-primary)",
  color: "var(--hs-text-primary)", outline: "none",
};
const textareaStyle = { ...inputStyle, resize: "vertical", minHeight: "80px", fontFamily: "inherit" };
const labelSm = { fontSize: "12px", color: "var(--hs-text-secondary)", marginBottom: "4px", display: "block" };
const sectionTitle = { fontWeight: "500", fontSize: "15px", color: "var(--hs-text-primary)" };
const sectionTitleBold = { fontWeight: "500", fontSize: "16px", color: "var(--hs-text-primary)" };

// ── MultiSelectDropdown ────────────────────────────────────────────────────────
function MultiSelectDropdown({ label, options, selected, onToggle, placeholder }) {
  const [open, setOpen] = useState(false);
  const display = selected.length === 0 ? placeholder : selected.join(", ");
  return (
    <div style={{ position: "relative", marginBottom: "12px", zIndex: open ? 500 : 1 }}>
      <label style={labelSm}>{label}</label>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ ...inputStyle, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none" }}
      >
        <span style={{
          color: selected.length ? "var(--hs-text-primary)" : "var(--hs-text-tertiary)",
          fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "90%",
        }}>{display}</span>
        <span style={{ color: "var(--hs-text-secondary)", fontSize: "11px", flexShrink: 0 }}>▾</span>
      </div>
      {open && (
        <div style={{
          position: "relative", zIndex: 200,
          background: "var(--hs-bg-primary)", border: "0.5px solid var(--hs-border-secondary)",
          borderRadius: "6px", marginTop: "6px", maxHeight: "70vh", overflowY: "auto", overscrollBehavior: "contain",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}>
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => onToggle(opt)}
              style={{
                display: "grid", gridTemplateColumns: "18px minmax(0, 1fr)", alignItems: "flex-start", columnGap: "8px",
                padding: "8px 12px", cursor: "pointer", fontSize: "14px",
                background: selected.includes(opt) ? TEAL_BG : "transparent",
                color: "var(--hs-text-primary)", textAlign: "left",
              }}
            >
              <input readOnly type="checkbox" checked={selected.includes(opt)} style={{ ...checkboxStyle, margin: "2px 0 0 0" }} />
              <span style={{ minWidth: 0, lineHeight: 1.35, wordBreak: "normal", overflowWrap: "break-word" }}>{opt}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ContractorMultiSelectDropdown({ contractors, selected, onChange, isLoading = false }) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const display = selected.length === 0 ? 'Select contractors on site' : selected.join(', ');
  const filteredContractors = useMemo(() => {
    const query = String(searchQuery || '').trim().toLowerCase();

    if (!query) {
      return contractors;
    }

    return contractors.filter((contractor) => [contractor.name, contractor.contact, contractor.email]
      .some((value) => String(value || '').toLowerCase().includes(query)));
  }, [contractors, searchQuery]);
  const toggleContractor = (contractorName) => {
    onChange(selected.includes(contractorName)
      ? selected.filter((name) => name !== contractorName)
      : [...selected, contractorName]);
  };

  return (
    <div style={{ position: "relative", marginBottom: "12px", zIndex: open ? 500 : 1 }}>
      <label style={labelSm}>Contractors on Site</label>
      <div
        onClick={() => setOpen((current) => !current)}
        style={{ ...inputStyle, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none" }}
      >
        <span style={{
          color: selected.length ? "var(--hs-text-primary)" : "var(--hs-text-tertiary)",
          fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "90%",
        }}>{display}</span>
        <span style={{ color: "var(--hs-text-secondary)", fontSize: "11px", flexShrink: 0 }}>▾</span>
      </div>
      {open && (
        <div style={{
          position: "relative", zIndex: 200,
          background: "var(--hs-bg-primary)", border: "0.5px solid var(--hs-border-secondary)",
          borderRadius: "6px", marginTop: "6px", maxHeight: "70vh", overflowY: "auto", overscrollBehavior: "contain",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}>
          <div style={{ padding: "8px 12px", borderBottom: "0.5px solid var(--hs-border-tertiary)", background: "var(--hs-bg-primary)", position: "sticky", top: 0, zIndex: 1 }}>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search contractors"
              style={{ ...inputStyle, marginBottom: 0 }}
            />
          </div>
          {contractors.length === 0 ? (
            <div style={{ padding: "8px 12px", fontSize: "13px", color: "var(--hs-text-secondary)" }}>
              {isLoading ? 'Loading contractors...' : 'No active contractors available'}
            </div>
          ) : filteredContractors.length === 0 ? (
            <div style={{ padding: "8px 12px", fontSize: "13px", color: "var(--hs-text-secondary)" }}>
              No contractors match your search
            </div>
          ) : filteredContractors.map((contractor) => (
            <div
              key={contractor.id || contractor.name}
              onClick={() => toggleContractor(contractor.name)}
              style={{
                display: "grid", gridTemplateColumns: "18px minmax(0, 1fr)", alignItems: "flex-start", columnGap: "8px",
                padding: "8px 12px", cursor: "pointer", fontSize: "14px",
                background: selected.includes(contractor.name) ? TEAL_BG : "transparent",
                color: "var(--hs-text-primary)", textAlign: "left",
              }}
            >
              <input readOnly type="checkbox" checked={selected.includes(contractor.name)} style={{ ...checkboxStyle, margin: "2px 0 0 0" }} />
              <span style={{ minWidth: 0, lineHeight: 1.35, wordBreak: "normal", overflowWrap: "break-word" }}>
                <span style={{ display: "block" }}>{contractor.name}</span>
                {contractor.contact ? (
                  <span style={{ display: "block", color: "var(--hs-text-secondary)", fontSize: "12px", marginTop: "2px" }}>{contractor.contact}</span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ToolboxMeetingMinutes() {
  const location = useLocation();
  const { user: authenticatedUser } = useAuth();
  const [bootstrap, setBootstrap] = useState({
    currentUser: null,
    departments: [],
    selectedDepartments: [],
    employees: [],
    courseCategories: []
  });
  const [meetingDate, setMeetingDate] = useState(INIT_DATE);
  const [meetingTime, setMeetingTime] = useState(INIT_TIME);
  const [meetingId, setMeetingId] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [inspectionReports, setInspectionReports] = useState([]);
  const [inspectionCategoryFilter, setInspectionCategoryFilter] = useState('');
  const [inspectionStartDate, setInspectionStartDate] = useState('');
  const [inspectionEndDate, setInspectionEndDate] = useState('');
  const [inspectionLocationFilter, setInspectionLocationFilter] = useState('');
  const [inspectionDeficienciesFilter, setInspectionDeficienciesFilter] = useState('');
  const [inspectionPage, setInspectionPage] = useState(1);
  const [incidentReports, setIncidentReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [trainingCategories, setTrainingCategories] = useState([]);
  const [toolboxHistory, setToolboxHistory] = useState([]);
  const [toolboxDrafts, setToolboxDrafts] = useState([]);
  const [activeTab, setActiveTab] = useState('minutes');
  const [deepLinkStatus, setDeepLinkStatus] = useState('');
  const [handledDeepLinkMeetingId, setHandledDeepLinkMeetingId] = useState('');
  const [topicPage, setTopicPage] = useState(1);
  const [topicSearch, setTopicSearch] = useState('');
  const [selectedDepts, setSelectedDepts] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [prequalificationContractorRecords, setPrequalificationContractorRecords] = useState([]);
  const [isLoadingContractors, setIsLoadingContractors] = useState(false);
  const [hasLoadedContractors, setHasLoadedContractors] = useState(false);
  const [minuteAttachments, setMinuteAttachments] = useState([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const [inspChecked, setInspChecked] = useState(false);
  const [selectedInsp, setSelectedInsp] = useState([]);
  const [inciChecked, setInciChecked] = useState(false);
  const [selectedInci, setSelectedInci] = useState([]);
  const [concernChecked, setConcernChecked] = useState(false);
  const [concernText, setConcernText] = useState("");
  const [sendCommittee, setSendCommittee] = useState(false);
  const [hsChecked, setHsChecked] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState({});
  const [topicText, setTopicText] = useState({});
  const [commitChecked, setCommitChecked] = useState(false);
  const [commitText, setCommitText] = useState("");
  const [attachChecked, setAttachChecked] = useState(false);
  const [correctiveActions, setCorrectiveActions] = useState([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const draftState = getStorageItem(DRAFT_STORAGE_KEY);
    if (draftState) {
      try {
        const parsed = JSON.parse(draftState);
        if (parsed && typeof parsed === 'object') {
          setMeetingDate(parsed.meetingDate || INIT_DATE);
          setMeetingTime(parsed.meetingTime || INIT_TIME);
          setSelectedDepts(Array.isArray(parsed.selectedDepts) ? parsed.selectedDepts : []);
          setSelectedEmployees(Array.isArray(parsed.selectedEmployees) ? parsed.selectedEmployees : []);
          setSelectedContractors(Array.isArray(parsed.selectedContractors) ? parsed.selectedContractors : []);
          setMinuteAttachments(normalizeAttachmentEntries(parsed.minuteAttachments));
          setInspChecked(!!parsed.inspChecked);
          setSelectedInsp(Array.isArray(parsed.selectedInsp) ? parsed.selectedInsp : []);
          setInciChecked(!!parsed.inciChecked);
          setSelectedInci(Array.isArray(parsed.selectedInci) ? parsed.selectedInci : []);
          setConcernChecked(!!parsed.concernChecked);
          setConcernText(parsed.concernText || '');
          setSendCommittee(!!parsed.sendCommittee);
          setHsChecked(!!parsed.hsChecked);
          setSelectedTopics(parsed.selectedTopics && typeof parsed.selectedTopics === 'object' ? parsed.selectedTopics : {});
          setTopicText(parsed.topicText && typeof parsed.topicText === 'object' ? parsed.topicText : {});
          setCommitChecked(!!parsed.commitChecked);
          setCommitText(parsed.commitText || '');
          setAttachChecked(!!parsed.attachChecked);
          setCorrectiveActions(Array.isArray(parsed.correctiveActions) ? parsed.correctiveActions : []);
          setAcknowledged(!!parsed.acknowledged);
          setSaved(!!parsed.saved);
          setTopicSearch(parsed.topicSearch || '');
          setTopicPage(Number.isFinite(parsed.topicPage) ? parsed.topicPage : 1);
          setActiveTab(parsed.activeTab || 'minutes');
        }
      } catch (error) {
        console.warn('Unable to restore toolbox draft state:', error);
      }
    }

    const loadBootstrap = async () => {
      try {
        const response = await API.get('/api/committeeMeetings/toolbox/context');
        if (!cancelled && response.data?.success) {
          const departments = Array.isArray(response.data.departments) ? response.data.departments : [];
          const employees = Array.isArray(response.data.employees) ? response.data.employees : [];
          const selectedDepartments = Array.isArray(response.data.selectedDepartments) ? response.data.selectedDepartments : [];

          setBootstrap({
            currentUser: response.data.currentUser || null,
            departments,
            selectedDepartments,
            employees,
            courseCategories: Array.isArray(response.data.courseCategories) ? response.data.courseCategories : []
          });

          if (selectedDepartments.length > 0) {
            setSelectedDepts(selectedDepartments);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setBootstrap({
            currentUser: null,
            departments: [],
            selectedDepartments: [],
            employees: [],
            courseCategories: []
          });
        }
      }
    };

    loadBootstrap();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPrequalificationContractors = async () => {
      setIsLoadingContractors(true);
      try {
        const response = await API.get('/api/contractorPrequalification/records');
        if (!cancelled) {
          setPrequalificationContractorRecords(Array.isArray(response.data?.records) ? response.data.records : []);
          setHasLoadedContractors(true);
        }
      } catch (error) {
        if (!cancelled) {
          setPrequalificationContractorRecords([]);
        }
        console.error('Unable to load contractor prequalification records for Toolbox contractors on site:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingContractors(false);
        }
      }
    };

    loadPrequalificationContractors();
    window.addEventListener('focus', loadPrequalificationContractors);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', loadPrequalificationContractors);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadReferenceData = async () => {
      const requests = await Promise.allSettled([
        API.get('/api/inspectionReports/committee/list'),
        API.get('/api/incidents/committee/list'),
        API.get('/api/users?scope=all'),
        API.get('/api/training/categories'),
        API.get('/api/committeeMeetings/minutes/library'),
        API.get('/api/committeeMeetings/minutes/drafts')
      ]);

      if (cancelled) {
        return;
      }

      const [inspectionResult, incidentResult, usersResult, categoriesResult, historyResult, draftsResult] = requests;

      if (inspectionResult.status === 'fulfilled' && inspectionResult.value.data) {
        const reports = Array.isArray(inspectionResult.value.data.reports)
          ? inspectionResult.value.data.reports.map(mapInspectionListItem).filter((item) => item.id)
          : [];
        setInspectionReports(reports);
      }

      if (incidentResult.status === 'fulfilled' && incidentResult.value.data) {
        const incidents = Array.isArray(incidentResult.value.data.incidents)
          ? incidentResult.value.data.incidents.map(mapIncidentListItem).filter((item) => item.id)
          : [];
        setIncidentReports(incidents);
      }

      if (usersResult.status === 'fulfilled' && usersResult.value.data) {
        const apiUsers = Array.isArray(usersResult.value.data.users) ? usersResult.value.data.users : [];
        setUsers(apiUsers);
      }

      if (categoriesResult.status === 'fulfilled' && categoriesResult.value.data) {
        const apiCategories = Array.isArray(categoriesResult.value.data.categories) ? categoriesResult.value.data.categories : [];
        setTrainingCategories(apiCategories.filter((category) => category.TOOLBOX_ELIGIBLE || category.toolboxEligible));
      }

      if (historyResult.status === 'fulfilled' && historyResult.value.data) {
        setToolboxHistory(mapMinutesHistoryResponse(historyResult.value.data));
      }

      if (draftsResult.status === 'fulfilled' && draftsResult.value.data) {
        const apiDrafts = Array.isArray(draftsResult.value.data.drafts) ? draftsResult.value.data.drafts : [];
        setToolboxDrafts(apiDrafts.map(mapMinutesDraftItem).filter((item) => item.meetingId));
      }
    };

    loadReferenceData();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const requestedMeetingId = new URLSearchParams(location.search).get('meetingId');
    if (!requestedMeetingId || handledDeepLinkMeetingId === requestedMeetingId) {
      return undefined;
    }

    let cancelled = false;
    const openToolboxPdf = async () => {
      setActiveTab('history');
      setDeepLinkStatus('Opening toolbox meeting PDF...');

      try {
        const matchedHistoryItem = toolboxHistory.find((item) => String(item.meetingId || '') === String(requestedMeetingId));
        let pdfUrl = matchedHistoryItem?.pdfUrl || '';

        if (!pdfUrl) {
          const response = await API.get(`/api/committeeMeetings/${encodeURIComponent(requestedMeetingId)}/minutes/pdf-link`);
          if (!response.data?.success) {
            throw new Error(response.data?.error || 'Unable to resolve toolbox meeting PDF');
          }
          pdfUrl = response.data.bundleUrl || '';
        }

        if (!pdfUrl) {
          throw new Error('No finalized toolbox meeting PDF is available for this meeting.');
        }

        if (!cancelled) {
          setHandledDeepLinkMeetingId(requestedMeetingId);
          window.location.assign(pdfUrl);
        }
      } catch (error) {
        if (!cancelled) {
          setHandledDeepLinkMeetingId(requestedMeetingId);
          setDeepLinkStatus(error?.response?.data?.error || error.message || 'Unable to open the toolbox meeting PDF.');
        }
      }
    };

    openToolboxPdf();

    return () => {
      cancelled = true;
    };
  }, [location.search, toolboxHistory, handledDeepLinkMeetingId]);

  const departmentNames = bootstrap.departments.length > 0
    ? bootstrap.departments.map((department) => department.name || department.NAME).filter(Boolean)
    : FALLBACK_DEPARTMENTS;

  const courseCategories = (trainingCategories.length > 0 ? trainingCategories : bootstrap.courseCategories).length > 0
    ? (trainingCategories.length > 0 ? trainingCategories : bootstrap.courseCategories).map((category) => ({
        id: category.CODE || category.code,
        label: category.NAME || category.name,
        sub: TOOLBOX_TOPIC_HINTS[category.CODE || category.code] || null
      })).filter((category) => category.id && category.label)
      .sort((left, right) => {
        const leftIsOther = String(left.label || '').trim().toLowerCase() === 'other';
        const rightIsOther = String(right.label || '').trim().toLowerCase() === 'other';
        if (leftIsOther && !rightIsOther) return 1;
        if (!leftIsOther && rightIsOther) return -1;
        return String(left.label).localeCompare(String(right.label));
      })
    : FALLBACK_TOOLBOX_CATEGORIES;

  const currentUserName = bootstrap.currentUser?.displayName
    || [bootstrap.currentUser?.firstName, bootstrap.currentUser?.lastName].filter(Boolean).join(' ')
    || bootstrap.currentUser?.username
    || bootstrap.currentUser?.email
    || authenticatedUser?.displayName
    || [authenticatedUser?.firstName, authenticatedUser?.lastName].filter(Boolean).join(' ')
    || authenticatedUser?.username
    || authenticatedUser?.email
    || 'Unknown user';

  const currentUserDepartment = bootstrap.currentUser?.department
    || bootstrap.currentUser?.departmentName
    || authenticatedUser?.department
    || authenticatedUser?.departmentName
    || 'Department unavailable';

  const employeeLookup = new Map((bootstrap.employees.length > 0 ? bootstrap.employees : []).map((employee) => {
    const employeeName = employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
    return [String(employeeName || '').trim().toLowerCase(), employee];
  }));

  const actualUsers = users.length > 0 ? users : bootstrap.employees;
  const departmentLookupNames = selectedDepts.length > 0 ? selectedDepts : departmentNames;
  const usersForAssigneeSelect = (selectedDepts.length > 0
    ? actualUsers.filter((user) => {
        const department = String(user.department || user.departmentName || '').trim();
        return selectedDepts.includes(department);
      })
    : actualUsers
  ).filter((user) => user && (user.email || user.username || user.name));

  const filteredTopicCategories = courseCategories.filter((category) => {
    const query = String(topicSearch || '').trim().toLowerCase();
    if (!query) {
      return true;
    }
    return String(category.label || '').toLowerCase().includes(query)
      || String(category.id || '').toLowerCase().includes(query)
      || String(category.sub || '').toLowerCase().includes(query);
  });
  const topicPageCount = Math.max(1, Math.ceil(filteredTopicCategories.length / TOOLBOX_CATEGORY_PAGE_SIZE));
  const safeTopicPage = Math.min(topicPage, topicPageCount);
  const visibleTopicCategories = filteredTopicCategories.slice((safeTopicPage - 1) * TOOLBOX_CATEGORY_PAGE_SIZE, safeTopicPage * TOOLBOX_CATEGORY_PAGE_SIZE);

  const buildParticipants = () => selectedEmployees
    .map((employeeName) => employeeLookup.get(String(employeeName || '').trim().toLowerCase()))
    .filter((employee) => employee && employee.email)
    .map((employee) => ({
      name: employee.name || employee.email,
      email: employee.email,
      department: employee.department || ''
    }));

  const visibleEmployees = actualUsers.filter((user) => departmentLookupNames.includes(String(user.department || user.departmentName || '').trim()));

  const activePrequalificationContractors = useMemo(() => prequalificationContractorRecords
    .filter((record) => String(record.status || record.STATUS || '').toUpperCase() !== 'ARCHIVED')
    .filter((record) => String(record.companyName || record.COMPANY_NAME || '').trim())
    .map(mapPrequalificationContractorOption)
    .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })), [prequalificationContractorRecords]);

  const availableContractors = useMemo(() => [
    ...HARD_CODED_CONTRACTORS,
    ...activePrequalificationContractors
  ].sort((left, right) => left.name.localeCompare(right.name, undefined, { numeric: true, sensitivity: 'base' })), [activePrequalificationContractors]);

  useEffect(() => {
    if (!hasLoadedContractors) {
      return;
    }

    const activeContractorNames = new Set(availableContractors.map((contractor) => contractor.name));
    setSelectedContractors((currentContractors) => {
      const nextContractors = currentContractors.filter((contractorName) => activeContractorNames.has(contractorName));
      const unchanged = nextContractors.length === currentContractors.length &&
        nextContractors.every((contractorName, index) => contractorName === currentContractors[index]);

      return unchanged ? currentContractors : nextContractors;
    });
  }, [availableContractors, hasLoadedContractors]);

  const buildReviewedTopics = () => Object.entries(selectedTopics)
    .filter(([, selected]) => !!selected)
    .map(([topicId]) => ({
      code: topicId,
      label: courseCategories.find((category) => category.id === topicId)?.label || topicId,
      details: topicText[topicId] || ''
    }));

  const formatInspectionLabel = (inspection) => inspection?.label
    || `${inspection?.date || ''}
• ${inspection?.dept || ''}
• ${inspection?.address || ''} — ${inspection?.name || ''}`.trim();

  const parseDraftValue = (value, fallback) => {
    if (value && typeof value === 'object') {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      try {
        return JSON.parse(value);
      } catch (error) {
        return fallback;
      }
    }

    return fallback;
  };

  const applyMinutesDraft = (draft) => {
    const minutes = parseDraftValue(draft?.minutes, {});
    setMeetingDate(minutes.date || draft?.meetingDate || INIT_DATE);
    setMeetingTime(minutes.time || draft?.meetingTime || INIT_TIME);
    setSelectedDepts(Array.isArray(minutes.selectedDepartments) ? minutes.selectedDepartments : selectedDepts);
    setSelectedEmployees(Array.isArray(minutes.selectedMembers) ? minutes.selectedMembers : []);
    setSelectedContractors(Array.isArray(minutes.selectedContractors) ? minutes.selectedContractors : []);
    const restoredAttachments = normalizeAttachmentEntries(parseDraftValue(draft?.attachments, []));
    setMinuteAttachments(restoredAttachments);
    setInspChecked(!!minutes?.newBusiness?.inspectionEnabled);
    setSelectedInsp(Array.isArray(minutes?.newBusiness?.selectedInsp)
      ? minutes.newBusiness.selectedInsp
      : (Array.isArray(minutes?.newBusiness?.inspections) ? minutes.newBusiness.inspections.map((inspection) => inspection.id || inspection.INSPECTION_ID).filter(Boolean) : []));
    setInciChecked(!!minutes?.newBusiness?.incidentsEnabled);
    setSelectedInci(Array.isArray(minutes?.newBusiness?.selectedInci)
      ? minutes.newBusiness.selectedInci
      : (Array.isArray(minutes?.newBusiness?.incidents) ? minutes.newBusiness.incidents.map((incident) => incident.id || incident.INCIDENT_ID).filter(Boolean) : []));
    setConcernChecked(!!minutes?.newBusiness?.workerConcernsEnabled);
    setConcernText(minutes?.newBusiness?.workerConcernsText || '');
    setSendCommittee(false);
    setHsChecked(!!minutes?.newBusiness?.trainingEnabled);
    setSelectedTopics(parseDraftValue(minutes?.newBusiness?.selectedTopics, {}));
    setTopicText(parseDraftValue(minutes?.newBusiness?.topicText, {}));
    setCommitChecked(!!minutes?.newBusiness?.proceduresEnabled);
    setCommitText(minutes?.newBusiness?.proceduresText || '');
    setAttachChecked(Array.isArray(restoredAttachments) && restoredAttachments.length > 0);
    const restoredCorrectiveActions = parseDraftValue(draft?.correctiveActions, []);
    setCorrectiveActions(Array.isArray(restoredCorrectiveActions) ? restoredCorrectiveActions : []);
    setAcknowledged(false);
    setSaved(!!draft);
    setMeetingId(draft?.meetingId || '');
    setActiveTab('minutes');
  };

  const buildMinutesPayload = () => ({
    date: meetingDate,
    time: meetingTime,
    location: 'Toolbox Health & Safety Meeting Minutes',
    quorumMet: false,
    adoptAgenda: false,
    adoptPreviousMinutes: false,
    selectedDepartments: selectedDepts,
    selectedMembers: selectedEmployees,
    selectedContractors,
    additionalItemsHasItems: 'no',
    additionalItemsText: '',
    adoptAgendaMovedBy: '',
    adoptPreviousMinutesMovedBy: '',
    previousMeetingMinutes: '',
    adjournmentTime: '',
    adjournmentDate: '',
    nextMeetingDate: '',
    nextMeetingTime: '',
    nextMeetingLocation: '',
    acknowledgement: acknowledged,
    newBusiness: {
      inspectionEnabled: inspChecked,
      selectedInsp,
      inspections: selectedInsp.map((inspectionId) => {
        const inspection = inspectionReports.find((item) => String(item.id) === String(inspectionId))
          || inspectionSelectionItems.find((item) => String(item.id) === String(inspectionId));
        return inspection ? { ...inspection, label: formatInspectionLabel(inspection) } : null;
      }).filter(Boolean),
      incidentsEnabled: inciChecked,
      selectedInci,
      incidents: selectedInci.map((incidentId) => incidentReports.find((incident) => String(incident.id) === String(incidentId))).filter(Boolean),
      workerConcernsEnabled: concernChecked,
      workerConcernsText: concernText,
      trainingEnabled: hsChecked,
      selectedTopics,
      topicText,
      trainingText: buildReviewedTopics().map((topic) => topic.details ? `${topic.label}: ${topic.details}` : topic.label).join('\n'),
      proceduresEnabled: commitChecked,
      proceduresText: commitText,
      feedbackEnabled: false,
      feedbackText: ''
    }
  });

  const ensureMeetingId = async () => {
    if (meetingId) {
      return meetingId;
    }

    const response = await API.post('/api/committeeMeetings/create', {
      meetingDate,
      meetingTime,
      location: 'Toolbox Health & Safety Meeting Minutes',
      createdBy: bootstrap.currentUser?.email || bootstrap.currentUser?.username || currentUserName,
    });

    if (!response.data?.success) {
      throw new Error(response.data?.error || 'Failed to create committee meeting');
    }

    setMeetingId(response.data.meetingId || '');
    return response.data.meetingId;
  };

  const loadDraftFromServer = async (draftMeetingId) => {
    if (!draftMeetingId) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await API.get(`/api/committeeMeetings/${encodeURIComponent(draftMeetingId)}/minutes/draft`);
      if (!response.data?.success || !response.data?.draft) {
        throw new Error(response.data?.message || 'Draft not found');
      }

      applyMinutesDraft({
        meetingId: draftMeetingId,
        ...response.data.draft
      });
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to load draft.');
    } finally {
      setIsSaving(false);
    }
  };

  const uploadMinutesAttachment = async (file) => {
    if (!file) {
      return;
    }

    setIsUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await API.post('/api/committeeMeetings/upload-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Unable to upload attachment');
      }

      setMinuteAttachments((current) => [
        ...current,
        {
          id: response.data.attachmentId || response.data.fileId || response.data.id || response.data.attachmentUrl || response.data.url,
          attachmentId: response.data.attachmentId || response.data.fileId || response.data.id || '',
          fileId: response.data.fileId || response.data.attachmentId || response.data.id || '',
          name: response.data.fileName || file.name,
          url: response.data.attachmentUrl || response.data.url || ''
        }
      ]);
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const refreshToolboxHistory = async () => {
    const response = await API.get('/api/committeeMeetings/minutes/library');
    setToolboxHistory(mapMinutesHistoryResponse(response.data));
  };

  const persistMinutes = async (finalize = false) => {
    if (isUploadingAttachment) {
      return;
    }

    setIsSaving(true);
    try {
      const activeMeetingId = await ensureMeetingId();
      const minutesPayload = buildMinutesPayload();
      const participants = buildParticipants();
      const correctedActionsPayload = correctiveActions.map((action) => ({
        section: 'toolbox',
        description: action.desc,
        assignedTo: action.assignedTo,
        targetDate: action.targetDate,
        completionDate: action.completionDate || null
      })).filter((action) => String(action.description || '').trim());
      const attachmentIds = getAttachmentIds(minuteAttachments);

      const endpoint = finalize
        ? `/api/committeeMeetings/${encodeURIComponent(activeMeetingId)}/minutes/finalize`
        : `/api/committeeMeetings/${encodeURIComponent(activeMeetingId)}/minutes/draft`;

      const body = finalize
        ? {
            minutes: minutesPayload,
            attachments: attachmentIds,
            correctiveActionIds: correctiveActions.map((action) => action.id),
            participants,
            addTraining: buildReviewedTopics().length > 0,
            selectedDepartments: selectedDepts,
            healthSafetyTopicsReviewed: buildReviewedTopics()
          }
        : {
            minutes: minutesPayload,
            attachments: attachmentIds,
            correctiveActions: correctedActionsPayload
          };

      const response = await API.post(endpoint, body);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Unable to save minutes');
      }

      if (finalize) {
        try {
          await refreshToolboxHistory();
        } catch (refreshError) {
          console.error('Unable to refresh toolbox history after finalize:', refreshError);
        }
        setSubmitted(true);
        removeStorageItem(DRAFT_STORAGE_KEY);
      } else {
        setSaved(true);
        setStorageItem(DRAFT_STORAGE_KEY, JSON.stringify({
          meetingId: activeMeetingId,
          meetingDate,
          meetingTime,
          selectedDepts,
          selectedEmployees,
          selectedContractors,
          minuteAttachments,
          inspChecked,
          selectedInsp,
          inciChecked,
          selectedInci,
          concernChecked,
          concernText,
          sendCommittee,
          hsChecked,
          selectedTopics,
          topicText,
          commitChecked,
          commitText,
          attachChecked,
          correctiveActions,
          acknowledged,
          saved: true,
          topicSearch,
          topicPage,
          activeTab
        }));
      }
    } catch (error) {
      window.alert(error?.response?.data?.error || error.message || 'Unable to save minutes.');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDept = (d) => {
    const next = selectedDepts.includes(d) ? selectedDepts.filter((x) => x !== d) : [...selectedDepts, d];
    setSelectedDepts(next);
    setSelectedEmployees([]);
  };
  const selectAllDepts = () => {
    setSelectedDepts(selectedDepts.length === departmentNames.length ? [] : [...departmentNames]);
    setSelectedEmployees([]);
  };

  const availableEmployees = [...new Set(visibleEmployees.map((employee) => employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim()).filter(Boolean))];

  const addCorrective = () =>
    setCorrectiveActions((p) => [...p, { id: Date.now(), desc: "", assignedTo: "", targetDate: "" }]);
  const removeCorrective = (id) =>
    setCorrectiveActions((p) => p.filter((a) => a.id !== id));
  const updateCorrective = (id, field, value) =>
    setCorrectiveActions((p) => p.map((a) => (a.id === id ? { ...a, [field]: value } : a)));
  const inspectionSelectionItems = inspectionReports;
  const inspectionCategoryOptions = [...new Set(inspectionSelectionItems.map(getInspectionCategory).filter(Boolean))].sort();
  const inspectionLocationOptions = [...new Set(inspectionSelectionItems.map(getInspectionLocation).filter(Boolean))].sort();
  const filteredInspectionSelectionItems = inspectionSelectionItems.filter((inspection) => {
    const category = getInspectionCategory(inspection);
    if (inspectionCategoryFilter && category !== inspectionCategoryFilter) {
      return false;
    }

    const dateOnly = getInspectionDateOnly(inspection);
    if (inspectionStartDate && dateOnly && dateOnly < inspectionStartDate) {
      return false;
    }
    if (inspectionEndDate && dateOnly && dateOnly > inspectionEndDate) {
      return false;
    }

    const locationLabel = getInspectionLocation(inspection);
    if (inspectionLocationFilter && locationLabel !== inspectionLocationFilter) {
      return false;
    }

    const hasDeficiencies = getInspectionHasDeficiencies(inspection);
    if (inspectionDeficienciesFilter === 'yes') {
      return hasDeficiencies;
    }
    if (inspectionDeficienciesFilter === 'no') {
      return !hasDeficiencies;
    }

    return true;
  });
  const inspectionPageCount = Math.max(1, Math.ceil(filteredInspectionSelectionItems.length / INSPECTION_REPORTS_PAGE_SIZE));
  const safeInspectionPage = Math.min(inspectionPage, inspectionPageCount);
  const inspectionPaginationStart = (safeInspectionPage - 1) * INSPECTION_REPORTS_PAGE_SIZE;
  const inspectionPaginationEnd = Math.min(inspectionPaginationStart + INSPECTION_REPORTS_PAGE_SIZE, filteredInspectionSelectionItems.length);
  const visibleInspectionSelectionItems = filteredInspectionSelectionItems.slice(inspectionPaginationStart, inspectionPaginationEnd);
  const incidentSelectionItems = incidentReports;
  const correctiveAssigneeOptions = usersForAssigneeSelect.map((user) => {
    const email = user.email || user.username || '';
    const name = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ');
    return {
      value: email || name,
      label: formatUserLabel(user)
    };
  }).filter((option) => option.value);

  const categoryPaginationStart = (safeTopicPage - 1) * TOOLBOX_CATEGORY_PAGE_SIZE;
  const categoryPaginationEnd = Math.min(categoryPaginationStart + TOOLBOX_CATEGORY_PAGE_SIZE, filteredTopicCategories.length);

  const changeTopicPage = (delta) => {
    setTopicPage((current) => {
      const next = current + delta;
      if (next < 1) {
        return 1;
      }
      if (next > topicPageCount) {
        return topicPageCount;
      }
      return next;
    });
  };

  const changeInspectionPage = (delta) => {
    setInspectionPage((current) => {
      const currentSafePage = Math.min(Math.max(1, current), inspectionPageCount);
      const next = currentSafePage + delta;
      if (next < 1) {
        return 1;
      }
      if (next > inspectionPageCount) {
        return inspectionPageCount;
      }
      return next;
    });
  };

  const isSubmitBlocked = isSaving || isUploadingAttachment;
  const canFinalize = acknowledged && !isSubmitBlocked;

  if (submitted) return (
    <div className="submitted-screen">
      <div style={{ fontSize: "48px", marginBottom: "12px" }}>✅</div>
      <h2 style={{ color: TEAL, margin: "0 0 8px" }}>Meeting Minutes Sent</h2>
      <p style={{ color: "var(--hs-text-secondary)" }}>All attendees have been notified by email.</p>
      <button className="btn-teal" onClick={() => setSubmitted(false)}>Start New Meeting</button>
    </div>
  );

  return (
    <div className="app-wrapper">
      <h2 className="sr-only">Toolbox Health and Safety Meeting Minutes form</h2>

      {/* ── Header ── */}
      <div className="top-header">
        <span className="header-title">Toolbox Health &amp; Safety Meeting Minutes</span>
        <span className="header-user">
          Logged in as: <strong>{currentUserName}</strong> &nbsp; {currentUserDepartment}
        </span>
      </div>

      <div className="page-body">
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
            ['minutes', 'Toolbox Minutes'],
            ['drafts', 'Drafts'],
            ['history', 'Toolbox History']
          ].map(([tabId, label]) => (
            <button
              key={tabId}
              type="button"
              className="btn-outline-teal"
              onClick={() => setActiveTab(tabId)}
              style={{ fontWeight: activeTab === tabId ? '600' : '400' }}
            >
              {label}
            </button>
          ))}
        </div>
        {deepLinkStatus && (
          <div style={{ background: TEAL_BG, border: `0.5px solid ${TEAL}`, borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', color: '#0f6e56', fontSize: '13px' }}>
            {deepLinkStatus}
          </div>
        )}
        {activeTab === 'drafts' ? (
          <div className="card" style={{ padding: '16px' }}>
            <h3 className="section-heading">Saved Drafts</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {['Date', 'Location', 'Updated By', 'Last Updated', 'Attachment Bundle', 'Action'].map((header) => (
                    <th key={header} style={{ textAlign: 'left', padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {toolboxDrafts.length > 0 ? toolboxDrafts.map((row) => (
                  <tr key={row.meetingId}>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{String(row.date || '').slice(0, 10)}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{row.location || 'Toolbox Health & Safety Meeting Minutes'}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{row.createdBy}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{String(row.updatedAt || '').replace('T', ' ').slice(0, 16)}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{row.attachmentCount > 0 ? `${row.attachmentCount} file(s)` : 'None'}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button type="button" className="btn-outline-teal" onClick={() => loadDraftFromServer(row.meetingId)}>
                          Load Draft
                        </button>
                        <button
                          type="button"
                          className="btn-outline-teal"
                          onClick={async () => {
                            const confirmed = window.confirm('Delete this draft? This cannot be undone.');
                            if (!confirmed) {
                              return;
                            }

                            try {
                              const response = await API.delete(`/api/committeeMeetings/minutes/drafts/${encodeURIComponent(row.meetingId)}`);
                              if (!response.data?.success) {
                                throw new Error(response.data?.error || 'Unable to delete draft');
                              }
                              setToolboxDrafts((current) => current.filter((draft) => draft.meetingId !== row.meetingId));
                              if (meetingId === row.meetingId) {
                                removeStorageItem(DRAFT_STORAGE_KEY);
                                setMeetingId('');
                                setSaved(false);
                              }
                            } catch (error) {
                              window.alert(error?.response?.data?.error || error.message || 'Unable to delete draft.');
                            }
                          }}
                        >
                          Delete Draft
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" style={{ padding: '12px', color: 'var(--hs-text-secondary)' }}>No saved drafts found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : activeTab === 'history' ? (
          <div className="card" style={{ padding: '16px' }}>
            <h3 className="section-heading">Toolbox History</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {['Date', 'Facilitator / User', 'Toolbox Meeting PDF'].map((header) => (
                    <th key={header} style={{ textAlign: 'left', padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {toolboxHistory.length > 0 ? toolboxHistory.map((row) => (
                  <tr key={row.meetingId}>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{String(row.date || '').slice(0, 10)}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>{row.facilitator}</td>
                    <td style={{ padding: '8px', borderBottom: '0.5px solid var(--hs-border-tertiary)' }}>
                      {row.pdfUrl ? <a href={row.pdfUrl} target="_blank" rel="noreferrer">View PDF</a> : 'Unavailable'}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="3" style={{ padding: '12px', color: 'var(--hs-text-secondary)' }}>No submitted toolbox meetings found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
        <>
        <div style={{ textAlign: "center", marginBottom: "14px" }}>
          <button type="button" className="back-link" onClick={() => window.history.back()}>← Back to Workflow Selection</button>
        </div>

        {/* ── Auto info ── */}
        <div className="card" style={{ padding: "14px 16px", overflow: "visible" }}>
          <div className="info-grid">
            {[["Initiator",currentUserName],["Department",currentUserDepartment],["Date",INIT_DATE],["Time",DISPLAY_TIME]].map(([k, v]) => (
              <div key={k}><span className="muted">{k}: </span><strong>{v}</strong></div>
            ))}
          </div>
        </div>

        {/* ── Date & Time ── */}
        <div className="card" style={{ padding: "14px 16px" }}>
          <div className="two-col">
            <div>
              <label style={labelSm}>Date</label>
              <input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelSm}>Time (Update if needed)</label>
              <input type="time" value={meetingTime} onChange={(e) => setMeetingTime(e.target.value)} style={inputStyle} />
            </div>
          </div>
        </div>

        {/* ── Department ── */}
          <div className="card" style={{ padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <label style={labelSm}>Select a department</label>
            <button className="btn-outline-teal" onClick={selectAllDepts}>
              {selectedDepts.length === departmentNames.length ? "Deselect All" : "Select All"}
            </button>
          </div>
          <div className="list-box">
            {departmentNames.map((d, i) => (
              <div
                key={d}
                onClick={() => toggleDept(d)}
                className="list-row"
                style={{
                  background: selectedDepts.includes(d) ? TEAL_BG : "var(--hs-bg-primary)",
                  borderTop: i > 0 ? "0.5px solid var(--hs-border-tertiary)" : "none",
                }}
              >
                <input readOnly type="checkbox" checked={selectedDepts.includes(d)} style={checkboxStyle} />
                {d}
              </div>
            ))}
          </div>
        </div>

        {/* ── Employees & Contractors ── */}
        <div className="card" style={{ padding: "14px 16px" }}>
          <MultiSelectDropdown
            label="Employees in Attendance"
            options={availableEmployees}
            selected={selectedEmployees}
            onToggle={(e) => setSelectedEmployees((p) => p.includes(e) ? p.filter((x) => x !== e) : [...p, e])}
            placeholder={selectedDepts.length === 0 ? "Select a department first" : "Select employees in attendance"}
          />
          <ContractorMultiSelectDropdown
            contractors={availableContractors}
            selected={selectedContractors}
            onChange={setSelectedContractors}
            isLoading={isLoadingContractors}
          />
        </div>

        {/* ── Toolbox Meeting Minutes card ── */}
        <div className="card" style={{ padding: "16px 16px 4px" }}>
          <h3 className="section-heading">Toolbox Meeting Minutes</h3>

          {/* Inspection Reports */}
          <div className="collapsible">
            <div style={sectionHeaderStyle(inspChecked)} onClick={() => setInspChecked((p) => !p)}>
              <input readOnly type="checkbox" checked={inspChecked} style={checkboxStyle} />
              <span style={{ ...sectionTitle, color: inspChecked ? "#0f6e56" : "var(--hs-text-primary)" }}>
                &nbsp;Inspection Reports
              </span>
            </div>
            {inspChecked && (
              <div className="collapsible-body">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px", marginBottom: "10px" }}>
                  <div>
                    <label style={labelSm}>Category</label>
                    <select value={inspectionCategoryFilter} onChange={(e) => { setInspectionCategoryFilter(e.target.value); setInspectionPage(1); }} style={inputStyle}>
                      <option value="">All categories</option>
                      {inspectionCategoryOptions.map((category) => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelSm}>Start Date</label>
                    <input type="date" value={inspectionStartDate} onChange={(e) => { setInspectionStartDate(e.target.value); setInspectionPage(1); }} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelSm}>End Date</label>
                    <input type="date" value={inspectionEndDate} onChange={(e) => { setInspectionEndDate(e.target.value); setInspectionPage(1); }} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelSm}>Location</label>
                    <select value={inspectionLocationFilter} onChange={(e) => { setInspectionLocationFilter(e.target.value); setInspectionPage(1); }} style={inputStyle}>
                      <option value="">All locations</option>
                      {inspectionLocationOptions.map((locationLabel) => (
                        <option key={locationLabel} value={locationLabel}>{locationLabel}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={labelSm}>Deficiencies</label>
                    <select value={inspectionDeficienciesFilter} onChange={(e) => { setInspectionDeficienciesFilter(e.target.value); setInspectionPage(1); }} style={inputStyle}>
                      <option value="">All</option>
                      <option value="yes">With deficiencies</option>
                      <option value="no">No deficiencies</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button
                      type="button"
                      className="btn-outline-teal"
                      onClick={() => {
                        setInspectionCategoryFilter('');
                        setInspectionStartDate('');
                        setInspectionEndDate('');
                        setInspectionLocationFilter('');
                        setInspectionDeficienciesFilter('');
                        setInspectionPage(1);
                      }}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
                <p className="hint">Select completed inspection reports to include:</p>
                {visibleInspectionSelectionItems.length > 0 ? visibleInspectionSelectionItems.map((r) => (
                  <div
                    key={r.id}
                    onClick={() => setSelectedInsp((p) => p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id])}
                    className="check-row"
                    style={{ background: selectedInsp.includes(r.id) ? TEAL_BG : "transparent" }}
                  >
                    <input readOnly type="checkbox" checked={selectedInsp.includes(r.id)} style={{ ...checkboxStyle, marginTop: "2px" }} />
                    <span className="muted" style={{ display: 'block', minWidth: 0, lineHeight: 1.35 }}>
                      <a
                        href={getInspectionPdfUrl(r)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        style={{ color: TEAL, textDecoration: 'underline' }}
                      >
                        {r.date || 'View Inspection PDF'}
                      </a>
                      <span style={{ display: 'block' }}>• {getInspectionCategory(r) || 'N/A'}</span>
                      <span style={{ display: 'block' }}>• {getInspectionLocation(r) || 'Location not recorded'}</span>
                    </span>
                  </div>
                )) : (
                  <p className="hint" style={{ marginBottom: 0 }}>No inspection reports match the current filters.</p>
                )}
                {filteredInspectionSelectionItems.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginTop: "10px", flexWrap: "wrap" }}>
                    <button type="button" className="btn-outline-teal" onClick={() => changeInspectionPage(-1)} disabled={safeInspectionPage <= 1}>
                      Previous
                    </button>
                    <span style={{ fontSize: "12px", color: "var(--hs-text-secondary)" }}>
                      Showing {inspectionPaginationStart + 1}-{inspectionPaginationEnd} of {filteredInspectionSelectionItems.length} inspection report{filteredInspectionSelectionItems.length === 1 ? '' : 's'} | Page {safeInspectionPage} of {inspectionPageCount}
                    </span>
                    <button type="button" className="btn-outline-teal" onClick={() => changeInspectionPage(1)} disabled={safeInspectionPage >= inspectionPageCount}>
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Incident Reports */}
          <div className="collapsible">
            <div style={sectionHeaderStyle(inciChecked)} onClick={() => setInciChecked((p) => !p)}>
              <input readOnly type="checkbox" checked={inciChecked} style={checkboxStyle} />
              <span style={{ ...sectionTitle, color: inciChecked ? "#0f6e56" : "var(--hs-text-primary)" }}>
                &nbsp;Incident and Investigation Reports
              </span>
            </div>
            {inciChecked && (
              <div className="collapsible-body">
                <p className="hint">Select incident reports to include:</p>
                {incidentSelectionItems
                  .filter((incident) => selectedDepts.length === 0 || selectedDepts.some((department) => String(incident.dept || '').toLowerCase().includes(String(department || '').toLowerCase())))
                  .map((r) => (
                  <div
                    key={r.id}
                    onClick={() => setSelectedInci((p) => p.includes(r.id) ? p.filter((x) => x !== r.id) : [...p, r.id])}
                    className="check-row"
                    style={{ background: selectedInci.includes(r.id) ? TEAL_BG : "transparent" }}
                  >
                    <input readOnly type="checkbox" checked={selectedInci.includes(r.id)} style={checkboxStyle} />
                    <span className="muted">{r.date}</span>
                    <span style={{ color: "#0f6e56" }}>• {r.type}</span>
                    <span className="muted">• {r.dept}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Worker Concerns */}
          <div className="collapsible">
            <div style={sectionHeaderStyle(concernChecked)} onClick={() => setConcernChecked((p) => !p)}>
              <input readOnly type="checkbox" checked={concernChecked} style={checkboxStyle} />
              <span style={{ ...sectionTitle, color: concernChecked ? "#0f6e56" : "var(--hs-text-primary)" }}>
                &nbsp;Worker Health &amp; Safety Concerns/Issues Reported
              </span>
            </div>
            {concernChecked && (
              <div className="collapsible-body">
                <textarea value={concernText} onChange={(e) => setConcernText(e.target.value)}
                  placeholder="Describe concerns or issues..." style={textareaStyle} />
                <div className="check-inline" onClick={() => setSendCommittee((p) => !p)}>
                  <input readOnly type="checkbox" checked={sendCommittee} style={checkboxStyle} />
                  <span className="check-label">Does the concern need to be sent to the committee?</span>
                </div>
                {sendCommittee && (
                  <p className="hint" style={{ marginLeft: "24px" }}>
                    ✉️ An email notification will be sent to the safety committee with the concern details, department, date and meeting info.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* H&S Topics */}
          <div className="collapsible">
            <div style={sectionHeaderStyle(hsChecked)} onClick={() => setHsChecked((p) => !p)}>
              <input readOnly type="checkbox" checked={hsChecked} style={checkboxStyle} />
              <span style={{ ...sectionTitleBold, color: hsChecked ? "#0f6e56" : "var(--hs-text-primary)" }}>
                &nbsp;Health &amp; Safety Topics Reviewed
              </span>
            </div>
            {hsChecked && (
              <div className="collapsible-body">
                <div className="teal-label-bar">Course Categories</div>
                <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "10px", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    value={topicSearch}
                    onChange={(event) => {
                      setTopicSearch(event.target.value);
                      setTopicPage(1);
                    }}
                    placeholder="Search training categories"
                    style={{ ...inputStyle, maxWidth: "320px" }}
                  />
                  <span style={{ fontSize: "12px", color: "var(--hs-text-secondary)" }}>
                    Showing {filteredTopicCategories.length === 0 ? 0 : categoryPaginationStart + 1}-{categoryPaginationEnd} of {filteredTopicCategories.length}
                  </span>
                </div>
                {visibleTopicCategories.map((cat) => (
                  <div key={cat.id} style={{ marginBottom: cat.sub ? "12px" : "4px" }}>
                    <div
                      className="check-inline"
                      onClick={() => setSelectedTopics((p) => ({ ...p, [cat.id]: !p[cat.id] }))}
                    >
                      <input readOnly type="checkbox" checked={!!selectedTopics[cat.id]} style={checkboxStyle} />
                      <span style={{ fontSize: "14px", color: "var(--hs-text-primary)" }}>{cat.label}</span>
                    </div>
                    {cat.sub && selectedTopics[cat.id] && (
                      <div style={{ marginLeft: "24px", marginTop: "6px" }}>
                        <label style={labelSm}>{cat.sub}</label>
                        <input
                          type="text"
                          value={topicText[cat.id] || ""}
                          onChange={(e) => setTopicText((p) => ({ ...p, [cat.id]: e.target.value }))}
                          placeholder={cat.sub + "..."}
                          style={inputStyle}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", marginTop: "10px", flexWrap: "wrap" }}>
                  <button type="button" className="btn-outline-teal" onClick={() => changeTopicPage(-1)} disabled={safeTopicPage <= 1}>
                    Previous
                  </button>
                  <span style={{ fontSize: "12px", color: "var(--hs-text-secondary)" }}>
                    Page {safeTopicPage} of {topicPageCount}
                  </span>
                  <button type="button" className="btn-outline-teal" onClick={() => changeTopicPage(1)} disabled={safeTopicPage >= topicPageCount}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Committee Summary */}
          <div className="collapsible">
            <div style={sectionHeaderStyle(commitChecked)} onClick={() => setCommitChecked((p) => !p)}>
              <input readOnly type="checkbox" checked={commitChecked} style={checkboxStyle} />
              <span style={{ ...sectionTitleBold, color: commitChecked ? "#0f6e56" : "var(--hs-text-primary)" }}>
                &nbsp;Committee Member Summary
              </span>
            </div>
            {commitChecked && (
              <div className="collapsible-body">
                <textarea
                  value={commitText}
                  onChange={(e) => setCommitText(e.target.value)}
                  placeholder="Add any feedback or summary from the previous committee meeting"
                  style={{ ...textareaStyle, minHeight: "100px" }}
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Corrective Actions ── */}
        <div style={{ marginBottom: "10px" }}>
          <button type="button" className="btn-corrective" onClick={addCorrective}>+ Add Corrective Action</button>
          {correctiveActions.length > 0 && (
            <div className="card" style={{ marginTop: "10px", padding: "12px 16px" }}>
              <h4 className="sub-heading">Corrective Actions</h4>
              {correctiveActions.map((ca, i) => (
                <div key={ca.id} className="corrective-card">
                  <div className="corrective-header">
                    <span style={{ fontWeight: "500", fontSize: "13px", color: TEAL }}>Corrective Action {i + 1}</span>
                    <button type="button" className="btn-remove" onClick={() => removeCorrective(ca.id)}>×</button>
                  </div>
                  <div className="two-col">
                    <div>
                      <label style={labelSm}>Description</label>
                      <textarea
                        value={ca.desc}
                        onChange={(e) => updateCorrective(ca.id, "desc", e.target.value)}
                        placeholder="Describe the corrective action..."
                        style={{ ...textareaStyle, minHeight: "70px" }}
                      />
                    </div>
                    <div>
                      <div style={{ marginBottom: "10px" }}>
                        <label style={labelSm}>Assigned To</label>
                        <select
                          value={ca.assignedTo}
                          onChange={(e) => updateCorrective(ca.id, "assignedTo", e.target.value)}
                          style={{ ...inputStyle, cursor: "pointer" }}
                        >
                          <option value="">Select person</option>
                            {correctiveAssigneeOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={labelSm}>Target Date</label>
                        <input
                          type="date"
                          value={ca.targetDate}
                          onChange={(e) => updateCorrective(ca.id, "targetDate", e.target.value)}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Attachments ── */}
        <div className="card" style={{ overflow: "hidden", marginBottom: "10px" }}>
          <div style={sectionHeaderStyle(attachChecked)} onClick={() => setAttachChecked((p) => !p)}>
            <input readOnly type="checkbox" checked={attachChecked} onChange={() => {}} style={checkboxStyle} />
            <span style={sectionTitle}>&nbsp;Add Attachments</span>
          </div>
          {attachChecked && (
            <div className="collapsible-body">
              <p className="hint" style={{ marginTop: 0 }}>
                Upload a signed roster or supporting document (PDF only). This file will be appended to the final minutes PDF sent to committee members.
              </p>
              <input
                type="file"
                accept=".pdf"
                style={{ fontSize: "13px", color: "var(--hs-text-primary)" }}
                onChange={async (event) => {
                  const file = event.target.files && event.target.files[0];
                  if (!file) {
                    return;
                  }

                  try {
                    await uploadMinutesAttachment(file);
                  } catch (error) {
                    window.alert(error?.response?.data?.error || error.message || 'Failed to upload attachment.');
                  } finally {
                    event.target.value = '';
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* ── Acknowledgement ── */}
        <div className="ack-box">
          <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "10px" }}>
            <span style={{ fontSize: "16px", flexShrink: 0 }}>⚠</span>
            <div>
              <strong className="ack-title">Final Acknowledgement</strong>
              <p className="ack-body">
                By signing off, you confirm that these meeting minutes are accurate and complete. The minutes will be sent to all committee members and staff for reference.
              </p>
            </div>
          </div>
          <label className="ack-label">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} style={checkboxStyle} />
            I acknowledge that the meeting minutes are accurate and complete
          </label>
        </div>

        {/* ── Submit buttons ── */}
        <div className="submit-row">
          <button className="btn-draft" type="button" disabled={isSubmitBlocked} onClick={() => persistMinutes(false)}>💾 Save as Draft</button>
          <button
            className="btn-submit"
            type="button"
            disabled={!canFinalize}
            onClick={() => canFinalize && persistMinutes(true)}
            style={{ background: canFinalize ? "#6366f1" : "#c0c4d6", cursor: canFinalize ? "pointer" : "not-allowed" }}
          >
              {isSaving ? 'Saving...' : isUploadingAttachment ? 'Uploading attachment...' : '✓ Complete & Send Minutes'}
          </button>
        </div>
        {saved && !submitted && (
          <p className="saved-msg">✅ Draft saved successfully.</p>
        )}
        {minuteAttachments.length > 0 && (
          <div className="card" style={{ marginTop: '10px', padding: '12px 16px' }}>
            <h4 className="sub-heading">Attached Files</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {minuteAttachments.map((attachment) => (
                <span key={attachment.id} className="tag">
                  {attachment.name}
                  {attachment.url ? (
                    <a href={attachment.url} target="_blank" rel="noreferrer" style={{ marginLeft: '8px' }}>View</a>
                  ) : null}
                  <span
                    className="tag-remove"
                    onClick={() => setMinuteAttachments((current) => current.filter((item) => item.id !== attachment.id))}
                  >
                    ×
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}